import React, { useState, useEffect } from "react";
import {
    Card, CardContent, Typography, Box, IconButton, Chip, Divider, Button, Tooltip,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { usePostDataFetch } from "../../utils/useDataFetch";
import { useAuth } from '../../context/AuthContext';

const getRelativeTime = (deadlineDateStr) => {
    if (!deadlineDateStr) return null;
    const deadline = new Date(deadlineDateStr);
    const now = new Date();
    const diffMs = deadline - now;

    if (diffMs <= 0) return "Frist abgelaufen";

    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    // Nur anzeigen wenn weniger als 7 Tage verbleiben
    if (days >= 7) return null;

    const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) return `Noch ${days} Tage, ${hours} Std.`;
    if (hours > 0) return `Noch ${hours} Std., ${minutes} Min.`;
    return `Noch ${minutes} Min.`;
};

const SingleTerminCard = ({ termin, isTrainer, onEdit, onDelete, refetch }) => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { postData: rsvpStatus, isLoading: isRsvpLoading } = usePostDataFetch();

    // Local State for independent updates
    const [localStatus, setLocalStatus] = useState(termin.mein_status);
    const [counts, setCounts] = useState({
        zusage: termin.counts_zusage || 0,
        absage: termin.counts_absage || 0,
        vielleicht: termin.counts_vielleicht || 0
    });

    // Notiz-Dialog State
    const [notizDialog, setNotizDialog] = useState({ open: false, pendingVariant: null });
    const [notizText, setNotizText] = useState('');

    const [timeLeft, setTimeLeft] = useState(getRelativeTime(termin.deadline));

    useEffect(() => {
        if (!termin.deadline) return;
        const timer = setInterval(() => {
            setTimeLeft(getRelativeTime(termin.deadline));
        }, 60000);
        return () => clearInterval(timer);
    }, [termin.deadline]);

    const handleRsvp = (variant) => {
        if (localStatus === variant) return;
        // Öffne Dialog für optionale Notiz
        setNotizText('');
        setNotizDialog({ open: true, pendingVariant: variant });
    };

    const confirmRsvp = async () => {
        if (!user?.id) {
            console.warn('RSVP aborted because no authenticated user is available.');
            setNotizDialog({ open: false, pendingVariant: null });
            return;
        }

        const variant = notizDialog.pendingVariant;
        const previousStatus = localStatus;

        // Optimistic UI Update
        setLocalStatus(variant);
        setCounts(prev => {
            const newCounts = { ...prev };
            if (previousStatus && newCounts[previousStatus] > 0) newCounts[previousStatus]--;
            newCounts[variant]++;
            return newCounts;
        });
        setNotizDialog({ open: false, pendingVariant: null });

        await rsvpStatus({
            url: "/termine/participate",
            aBody: { termin_id: termin.id, variant, user_id: user.id, notiz: notizText || null },
            skipAuth: false
        });
    };

    const formattedDate = termin.dat
        ? new Date(termin.dat).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
        : termin.wochentag;

    const formattedTime = `${termin.start_time.substring(0, 5)} - ${termin.end_time?.substring(0, 5)}`;

    return (
        <>
            <Card sx={{
                bgcolor: theme => theme.palette.mode === 'dark' ? 'grey.800' : 'background.paper',
                borderRadius: "12px",
                boxShadow: 2,
                border: '1px solid',
                borderColor: 'divider',
                cursor: 'pointer',
                transition: '0.3s',
                '&:hover': { boxShadow: 6, transform: 'translateY(-2px)' }
            }} onClick={() => navigate(`/termine/${termin.id}`)}>
                <CardContent sx={{ p: 2.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, alignItems: 'flex-start' }}>
                        <Typography variant="h6" fontWeight="bold">{termin.titel}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {isTrainer && (
                                <>
                                    <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); onEdit(termin); }}>
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); onDelete(termin.id); }}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </>
                            )}
                            <Chip
                                label={termin.typ}
                                color={termin.typ === 'Spiel' ? 'error' : termin.typ === 'Training' ? 'primary' : 'default'}
                                size="small"
                                sx={{ ml: 1, fontWeight: 'bold' }}
                            />
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', ml: 1, mr: 1 }}>
                        <Typography variant="body1" color="text.primary" sx={{ fontWeight: 500 }}>
                            {formattedDate}
                        </Typography>
                        <Typography variant="body1" color="text.primary" sx={{ fontWeight: 500, gap: 1 }}>
                            <AccessTimeIcon fontSize="inherit" /> {formattedTime}
                            {termin.ort && ` | ${termin.ort}`}
                        </Typography>
                    </Box>

                    {termin.deadline && timeLeft && (
                        <Typography variant="caption" color={timeLeft === "Frist abgelaufen" ? "text.secondary" : "error.main"} sx={{ display: 'flex', alignItems: 'center', mt: 1.5, gap: 0.5, fontWeight: 'bold' }}>
                            <AccessTimeIcon fontSize="inherit" />
                            {timeLeft}

                        </Typography>
                    )}

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ display: 'flex', gap: 1.5 }} onClick={(e) => e.stopPropagation()}>
                        <Button
                            variant={localStatus === 'zusage' ? 'contained' : 'outlined'}
                            color="success" size="small" startIcon={<CheckCircleOutlineIcon />}
                            onClick={() => handleRsvp('zusage')}
                            disabled={isRsvpLoading}
                            sx={{ borderRadius: "12px", minWidth: 85 }}
                        >
                            {counts.zusage}
                        </Button>

                        <Button
                            variant={localStatus === 'absage' ? 'contained' : 'outlined'}
                            color="error" size="small" startIcon={<HighlightOffIcon />}
                            onClick={() => handleRsvp('absage')}
                            disabled={isRsvpLoading}
                            sx={{ borderRadius: "12px", minWidth: 85 }}
                        >
                            {counts.absage}
                        </Button>

                        <Button
                            variant={localStatus === 'vielleicht' ? 'contained' : 'outlined'}
                            color="warning" size="small" startIcon={<HelpOutlineIcon />}
                            onClick={() => handleRsvp('vielleicht')}
                            disabled={isRsvpLoading}
                            sx={{ borderRadius: "12px", minWidth: 85 }}
                        >
                            {counts.vielleicht}
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            {/* Notiz-Dialog */}
            <Dialog
                open={notizDialog.open}
                onClose={() => setNotizDialog({ open: false, pendingVariant: null })}
                fullWidth
                maxWidth="xs"
                onClick={(e) => e.stopPropagation()}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    {notizDialog.pendingVariant === 'zusage' ? '✅ Zusage' :
                        notizDialog.pendingVariant === 'absage' ? '❌ Absage' : '❓ Vielleicht'}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Möchtest du eine kurze Notiz hinterlassen? (optional)
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        rows={2}
                        placeholder="z.B. Komme etwas später..."
                        value={notizText}
                        onChange={(e) => setNotizText(e.target.value)}
                        inputProps={{ maxLength: 200 }}
                        size="small"
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setNotizDialog({ open: false, pendingVariant: null })} color="inherit" size="small">
                        Abbrechen
                    </Button>
                    <Button onClick={confirmRsvp} variant="contained" size="small">
                        Bestätigen
                    </Button>
                </DialogActions>
            </Dialog>
        </>);
};

export default SingleTerminCard;
